import { XBOT_FEEDBACK_NOTE_LIMIT, XbotFeedbackBody, xbotFeedbackBodyOf, XbotMessage, XbotVerdict } from "@dto/pyxbot-agent.dto";
import { Button, Input, Modal, Tooltip } from "antd";
import { FC, useMemo, useState } from "react";
import { LuCopy, LuMaximize2, LuThumbsDown, LuThumbsUp } from "react-icons/lu";
import { askXbot, isObject, postJson, readJson, xbotFetch, XbotSend } from "../xbotFetch";
import { XBOT_CHAT_BASE } from "./chatClient";
import { exportActions } from "./ExportBar";
import { answerTextExport } from "./exportData";
import { XbotLoadPage, XbotOnOpenLink } from "./host";
import { hasOwnExport, XbotAnswer, XbotAnswerProps } from "./XbotAnswer";

export const EXPAND_LABEL = "Show this answer larger";
/** The window is as wide as the answer needs (xbot.less sets its least and a chart's width). */
export const EXPANDED_WIDTH = "fit-content";

export interface XbotAnswerExpandProps {
  message: XbotMessage;
  loadPage?: XbotLoadPage;
  onOpenLink?: XbotOnOpenLink;
  /** The question the answer replied to: the window's title. */
  question?: string;
  /** lets tests render it already open, since they can't press the button */
  initiallyOpen?: boolean;
}

/** The larger view: a mark in the answer's top right corner, an icon alone, shown on hover. */
export const XbotAnswerExpand: FC<XbotAnswerExpandProps> = ({ message, loadPage, onOpenLink, question, initiallyOpen }) => {
  // defaulted in the body: the React Compiler skips a component with a default in its destructured props
  const [open, setOpen] = useState(initiallyOpen ?? false);
  const close = () => setOpen(false);
  // Closes the modal before opening the link, so the page is never left underneath it.
  const openLink = (path: string) => {
    close();
    onOpenLink?.(path);
  };
  return <>
    <Tooltip title={EXPAND_LABEL}>
      <Button
        type="text"
        size="small"
        className="xm-xbot-expand xm-xbot-expand-corner"
        aria-label={EXPAND_LABEL}
        icon={<LuMaximize2 />}
        onClick={() => setOpen(true)}
      />
    </Tooltip>
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      centered
      destroyOnHidden
      width={EXPANDED_WIDTH}
      title={question?.trim() ? question.trim() : "Answer"}
      className="xm-xbot-expanded xm-xbot-look"
      // the table takes the screen's height itself (TableBlock), so the window may grow to it
      styles={{ body: { maxHeight: "calc(100vh - 120px)", overflow: "auto" } }}
      // Stops Esc from bubbling to the chat window's own Esc handler, which would close the chat too.
      modalRender={(panel) => <div onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.nativeEvent.stopPropagation();
          close();
        }
      }}>{panel}</div>}
    >
      {open ? <XbotAnswer message={message} loadPage={loadPage} onOpenLink={openLink} wide /> : null}
    </Modal>
  </>;
};

/** Thumb up/down under an agent answer: POST /api/pyxbot/v1/feedback {thread_id, turn, message_id?, verdict, note?} - no answer text travels with it. */
export const XBOT_FEEDBACK_URL = `${XBOT_CHAT_BASE}/feedback`;

// "gone" isn't a failure: the bot only keeps a thread while running, so a pre-restart answer has nothing to rate against.
export type FeedbackResult = "taken" | "gone" | "failed";

/** The longest thread id the service takes. */
const THREAD_ID_LIMIT = 200;

/** The turn an answer can be rated as, or null when it can't: still running, or missing its thread/place in it. */
export function rateableTurn(message: XbotMessage | null | undefined): { threadId: string; turnSeq: number; messageId: string } | null {
  if (!message || typeof message !== "object" || message.state === "pending") {
    return null;
  }
  const threadId = typeof message.thread_id === "string" ? message.thread_id.trim() : "";
  const turnSeq = message.turn_seq;
  const messageId = typeof message.id === "string" ? message.id : "";
  if (!threadId || threadId.length > THREAD_ID_LIMIT || !Number.isInteger(turnSeq) || turnSeq < 1) {
    return null;
  }
  return { threadId, turnSeq, messageId };
}

/** What one reply means: filed on 2xx+ok, "gone" when the turn is no longer held or ratings aren't taken here, else a retryable failure. */
export function feedbackResultOf(status: number, payload: unknown): FeedbackResult {
  if (status === 404 || status === 410 || status === 403) {
    return "gone";
  }
  if (status < 200 || status >= 300 || !isObject(payload)) {
    return "failed";
  }
  const ok = payload.ok === true || (isObject(payload.data) && payload.data.ok === true);
  return ok ? "taken" : "failed";
}

/** Sends one rating. Never rejects: a rating that does not arrive is quiet, like in the older chat. */
export async function sendXbotFeedback(body: XbotFeedbackBody, send: XbotSend = xbotFetch): Promise<FeedbackResult> {
  const response = await askXbot(XBOT_FEEDBACK_URL, postJson(body), send);
  return response ? feedbackResultOf(response.status, await readJson(response)) : "failed";
}

export type FeedbackStage = "idle" | "note" | "sending" | "sent" | "gone";

/** Any outcome besides taken/gone resets to idle so the reader can try again. */
export const STAGE_AFTER: Readonly<Record<FeedbackResult, FeedbackStage>> = { taken: "sent", gone: "gone", failed: "idle" };

interface RowProps {
  stage: FeedbackStage;
  note: string;
  onNote: (text: string) => void;
  onUp: () => void;
  onDown: () => void;
  onSend: () => void;
}

/** Right or wrong as drawn at one stage; a thumb down first asks what it should have been. */
export const FeedbackRow: FC<RowProps> = ({ stage, note, onNote, onUp, onDown, onSend }) => {
  if (stage === "gone") {
    return null;
  }
  if (stage === "sent") {
    return <div className="xm-chat-feedback"><span className="xm-chat-feedback-thanks">Thanks</span></div>;
  }
  const busy = stage === "sending";
  const thumbs = [["This answer is right", LuThumbsUp, onUp], ["This answer is wrong", LuThumbsDown, onDown]] as const;
  return <div className="xm-chat-feedback">
    <div className="xm-chat-feedback-icons">
      {thumbs.map(([label, Icon, onClick]) => <Tooltip key={label} title={label}>
        <Button type="text" size="small" aria-label={label} icon={<Icon />} disabled={busy} onClick={onClick} />
      </Tooltip>)}
    </div>
    {stage === "note" || busy ? <div className="xm-chat-feedback-note">
      <Input
        size="small"
        maxLength={XBOT_FEEDBACK_NOTE_LIMIT}
        value={note}
        placeholder="What should it have been?"
        disabled={busy}
        onChange={(e) => onNote(e.target.value)}
        onPressEnter={onSend}
      />
      <Button type="primary" size="small" loading={busy} onClick={onSend}>Send</Button>
    </div> : null}
  </div>;
};

export interface XbotAnswerToolsProps {
  message: XbotMessage;
  /** Right or wrong is offered only where NG does not run as production. */
  ratings: boolean;
}

export const XbotAnswerTools: FC<XbotAnswerToolsProps> = ({ message, ratings }) => {
  const [stage, setStage] = useState<FeedbackStage>("idle");
  const [note, setNote] = useState("");
  // The larger view is in the answer's corner (XbotFramedAnswer); under the answer only the rating.
  if (!ratings) {
    return null;
  }
  const turn = rateableTurn(message);
  const send = async (verdict: XbotVerdict, said: string) => {
    setStage("sending");
    setStage(STAGE_AFTER[await sendXbotFeedback(xbotFeedbackBodyOf(turn, verdict, said))]);
  };
  return <div className="xm-xbot-answer-tools">
    {turn ? <FeedbackRow
      stage={stage}
      note={note}
      onNote={setNote}
      onUp={() => void send("correct", "")}
      onDown={() => setStage("note")}
      onSend={() => void send("wrong", note)}
    /> : null}
  </div>;
};

export interface XbotFramedAnswerProps extends XbotAnswerProps {
  /** The question the answer replied to (the enlarged window's title). */
  question?: string;
}

const COPY_LABEL = "Copy the text";

/** A text answer's Copy, beside the larger view in its corner. */
const XbotCornerCopy: FC<{ message: XbotMessage }> = ({ message }) => {
  const [busy, setBusy] = useState(false);
  const copy = useMemo(() => exportActions(() => Promise.resolve(answerTextExport(message.blocks)), { name: "answer" }).copy, [message.blocks]);
  return <Tooltip title={COPY_LABEL}>
    <Button
      type="text"
      size="small"
      className="xm-xbot-copy-corner"
      aria-label={COPY_LABEL}
      icon={<LuCopy />}
      loading={busy}
      onClick={() => {
        setBusy(true);
        void copy().finally(() => setBusy(false));
      }}
    />
  </Tooltip>;
};

/** True when the answer's Copy goes to its corner: it has text and no block with a Copy of its own. */
export function copiesInCorner(message: XbotMessage): boolean {
  const blocks = Array.isArray(message.blocks) ? message.blocks : [];
  return !hasOwnExport(blocks) && blocks.some((block) => block?.type === "text" && block.state === "ready");
}

/**
 * The answer in its bubble. A finished answer carries the larger view as a mark in its top right corner,
 * shown on hover, as a photo gallery opens a picture, and a text answer its Copy beside it.
 */
export const XbotFramedAnswer: FC<XbotFramedAnswerProps> = ({ question, ...answer }) => {
  const settled = (answer.progress?.outcome ?? "done") !== "running";
  // The same frame while the answer streams and once it is done, so its blocks stay as they are (rows read, a
  // chart's pages) when it settles; only the corner's tools come with the end.
  const copy = settled && copiesInCorner(answer.message);
  return <div className={copy ? "xm-xbot-answer-framed has-copy" : "xm-xbot-answer-framed"}>
    {settled ? <div className="xm-xbot-corner-tools">
      {copy ? <XbotCornerCopy message={answer.message} /> : null}
      <XbotAnswerExpand message={answer.message} question={question} loadPage={answer.loadPage} onOpenLink={answer.onOpenLink} />
    </div> : null}
    <XbotAnswer {...answer} />
  </div>;
};
